// src/data/amenityCatalog.js
//
// EXPERIENCE-TAB: Client-side amenity catalog constants + pure scoring helpers.
// Mirrors server/lib/amenityCatalog.js (pure JS, no server imports).
// Both files must stay in sync if weights or rules change.

// ─── Catalog ─────────────────────────────────────────────────────────────────

export const CATEGORY_ORDER = ["Food & Drink", "Fitness & Wellness", "Spaces", "Commute", "IT", "Other"];

export const AMENITY_CATALOG = [
  { key: "hot_lunch",      label: "Hot Lunch",            weight: 20, hasFrequency: true,  hasCount: false, category: "Food & Drink"       },
  { key: "gym",            label: "Gym / Fitness",         weight: 15, hasFrequency: false, hasCount: false, category: "Fitness & Wellness" },
  { key: "barista",        label: "Barista Coffee Bar",   weight: 12, hasFrequency: false, hasCount: false, category: "Food & Drink"       },
  { key: "snack_bars",     label: "Snack Bars",           weight: 10, hasFrequency: false, hasCount: true,  category: "Food & Drink"       },
  { key: "rooftop",        label: "Rooftop / Outdoor",    weight: 10, hasFrequency: false, hasCount: false, category: "Spaces"             },
  { key: "massage",        label: "Wellness / Massage",   weight:  8, hasFrequency: true,  hasCount: false, category: "Fitness & Wellness" },
  { key: "mothers_room",   label: "Mother's Room",        weight:  8, hasFrequency: false, hasCount: false, category: "Fitness & Wellness" },
  { key: "it_bar",         label: "IT Bar",               weight:  8, hasFrequency: true,  hasCount: false, category: "IT"                 },
  { key: "game_room",      label: "Game Room",            weight:  7, hasFrequency: false, hasCount: false, category: "Spaces"             },
  { key: "library",        label: "Library / Focus Room", weight:  5, hasFrequency: false, hasCount: false, category: "Spaces"             },
  { key: "bike_commuter",  label: "Bike / Commuter Hub",  weight:  5, hasFrequency: false, hasCount: false, category: "Commute"            },
  { key: "parking",        label: "Parking",              weight:  5, hasFrequency: false, hasCount: false, category: "Commute"            },
  { key: "it_vending",     label: "IT Vending Machine",   weight:  4, hasFrequency: false, hasCount: false, category: "IT"                 },
];

export const BASKET_TOTAL = AMENITY_CATALOG.reduce((s, a) => s + a.weight, 0); // 117

export const CATALOG_KEYS = new Set(AMENITY_CATALOG.map((a) => a.key));

// ─── Factor weights ───────────────────────────────────────────────────────────
// Three-factor Experience model. When commute/attendance data are absent the
// available factors are renormalized to sum to 100 % so the amenity-only path
// produces the same 0–100 scale result as the old single-factor path.

export const FACTOR_WEIGHTS = {
  amenities: 65,
  commute:   20,
  space:     15,
};

// ─── Frequency multiplier ─────────────────────────────────────────────────────

function frequencyMultiplier(key, daysPerWeek) {
  const d = Math.max(0, Math.min(7, daysPerWeek ?? 0));
  if (key === "hot_lunch") {
    if (d >= 5) return 1.0;
    if (d >= 3) return 0.70;
    if (d >= 2) return 0.45;
    if (d >= 1) return 0.25;
    return 0;
  }
  return Math.min(1, d / 5);
}

// ─── Band ─────────────────────────────────────────────────────────────────────

export function experienceBand(subScore) {
  if (subScore >= 80) return "Excellent";
  if (subScore >= 60) return "Good";
  return "Limited";
}

// ─── Commute sub-score ────────────────────────────────────────────────────────
// Maps median commute minutes → 0–100 score (lower median = better experience).

export function commuteSubScoreFromMin(medianMin) {
  if (medianMin == null) return null;
  if (medianMin <= 15) return 100;
  if (medianMin <= 30) return 80;
  if (medianMin <= 45) return 60;
  if (medianMin <= 60) return 40;
  return 20;
}

// ─── Space-comfort sub-score ──────────────────────────────────────────────────
// Maps headcount / installed-capacity sharing ratio → 0–100 score.
// Lower ratio = more available seats = better comfort.

export function spaceSubScoreFromRatio(sharingRatio) {
  if (sharingRatio == null) return null;
  if (sharingRatio <= 0.70) return 100;
  if (sharingRatio <= 0.85) return 80;
  if (sharingRatio <= 1.00) return 65;
  if (sharingRatio <= 1.20) return 45;
  return 25;
}

// ─── Score ────────────────────────────────────────────────────────────────────

/**
 * scoreExperience(amenities, commuteData?, attendanceData?)
 *
 * Three-factor experience model:
 *   Amenities   65 % — weighted catalog score (existing logic)
 *   Commute     20 % — median commute banding (absent → excluded & reweighted)
 *   Space       15 % — headcount/capacity sharing ratio (absent → excluded)
 *
 * Backward-compatible: old call-sites pass only `amenities`; the new
 * args default to null so commute and space are excluded and the result
 * equals the former single-factor score.
 *
 * @param {object|null} amenities       JSONB map from location_amenities
 * @param {{median_min: number}|null}  commuteData
 * @param {{sharingRatio: number}|null} attendanceData
 *
 * @returns {{
 *   subScore: number,
 *   band: string,
 *   confidence: string,
 *   amenitySubScore: number,
 *   amenityCount: number,
 *   commuteSubScore: number|null,
 *   spaceSubScore: number|null,
 * }}
 */
export function scoreExperience(amenities, commuteData = null, attendanceData = null) {
  // ── Factor 1: Amenities ──────────────────────────────────────────────────
  let amenitySubScore = 0;
  let amenityCount    = 0;
  let anyPresent      = false;
  let confidence      = "no-data";

  if (amenities && typeof amenities === "object") {
    let earnedWeight = 0;

    for (const entry of AMENITY_CATALOG) {
      const row = amenities[entry.key];
      if (!row?.present) continue;
      anyPresent = true;
      amenityCount++;

      if (entry.hasFrequency) {
        earnedWeight += entry.weight * frequencyMultiplier(entry.key, row.detail?.daysPerWeek);
      } else {
        earnedWeight += entry.weight;
      }
    }

    // An amenity row that exists (even with nothing checked) counts as "computed"
    // so the factor participates in the weighted sum (with score 0) rather than
    // being excluded. "no-data" is reserved for a completely missing row.
    confidence      = "computed";
    amenitySubScore = Math.round((earnedWeight / BASKET_TOTAL) * 1000) / 10;
  }

  if (confidence === "no-data") {
    // No amenity row at all — fast-path return (same as old behavior)
    return {
      subScore: 0,
      band: "Limited",
      confidence: "no-data",
      amenitySubScore: 0,
      amenityCount: 0,
      commuteSubScore: null,
      spaceSubScore: null,
    };
  }

  // ── Factor 2: Commute ────────────────────────────────────────────────────
  const commuteSubScore = commuteSubScoreFromMin(commuteData?.median_min ?? null);

  // ── Factor 3: Space Comfort ──────────────────────────────────────────────
  const spaceSubScore = spaceSubScoreFromRatio(attendanceData?.sharingRatio ?? null);

  // ── Weighted composite (renormalize absent factors) ───────────────────────
  const { amenities: W_A, commute: W_C, space: W_S } = FACTOR_WEIGHTS;
  let totalW = W_A;
  if (commuteSubScore !== null) totalW += W_C;
  if (spaceSubScore   !== null) totalW += W_S;

  const norm = 100 / totalW;
  let composite = amenitySubScore * W_A * norm / 100;
  if (commuteSubScore !== null) composite += commuteSubScore * W_C * norm / 100;
  if (spaceSubScore   !== null) composite += spaceSubScore   * W_S * norm / 100;

  const subScore = Math.round(composite * 10) / 10;

  return {
    subScore,
    band: experienceBand(subScore),
    confidence,
    amenitySubScore,
    amenityCount,
    commuteSubScore,
    spaceSubScore,
  };
}

// ─── Rationale string ─────────────────────────────────────────────────────────

export function buildRationaleString(amenities, band) {
  if (!amenities || typeof amenities !== "object") {
    return "No amenities recorded yet for this location.";
  }

  const presentLabels = AMENITY_CATALOG
    .filter((a) => amenities[a.key]?.present)
    .map((a) => a.label);

  if (presentLabels.length === 0) {
    return "No amenities recorded yet for this location.";
  }

  const count = presentLabels.length;
  const total = AMENITY_CATALOG.length;
  return `${band} experience - ${count} of ${total} amenities confirmed on-site.`;
}
